import { stockParts } from './planet-stock.js';
import { LIBRARY_STOCK } from './planet-library.js';
import { sceneErrors, composePlanet as composeScenes } from './planet-scenes.js';
import { settingErrors, composeSetting } from './planet-setting.js';
export function composePlanet(spec) {
  const scenes=composeScenes(spec);
  return composeSetting(scenes,placeThings(scenes.things));
}
// A planet spec: everything that makes a little planet someone's own, in one small JSON. Steve's is
// built from _data/ by tools/planet.mjs; other people's will be written by their own AI through the
// Planets connector (My Tiny Planet, Linear P-PRO-40). The same rules for the server and the
// browser, so a spec the world draws is always one this file passed. Plain JavaScript, no imports.
//
//   {
//     version: 1,
//     slug: 'steve',                       the planet's name in its address: a-z, 0-9 and -
//     who: { name, sign },                 sign: the neon over the hub, 12 characters at most
//     links: [{ id, label, note, href }],  the hub's menu; href https://, mailto: or a site path
//     look: { theme, home, ground, sky },  theme from THEMES; home: a city, never an address;
//                                          ground: { color, hills 0..1 }; sky: { color, light, period?, clouds? }
//     things: [{ id, name, note, collection, at: { bearing, distance }, turn, scale, solid, model, stock, use,
//                parts: [{ shape, size, at, turn, color, finish, spin, text, round, profile,
//                          outline, path, thick, repeat, mirror }], light }],
//                                          what stands on someone else's planet, composed by
//                                          their own AI from simple shapes (SHAPES, FINISHES),
//                                          in metres, placed from where you land; model: the
//                                          thing in words, for a real 3D model (Meshy) that
//                                          takes the parts' place once it's made; stock: one
//                                          of the ready models (STOCK) instead; use: what a
//                                          tap does when you get there (USES)
//     collections: { <id>: { title, items: [...] } | { title, src } },
//                                          what matters to them: plain lists (items: text, or
//                                          small objects), or a site path that serves the list
//     places: [{ id, kind, displays: [{ kind, collection }], collection }],
//                                          what's built on the planet (PLACES), and how each
//                                          shows its collections (DISPLAYS)
//     robot: { lines, ask },               what the hub's robot says, and what to ask it
//     telescope: { engraving, pins: [{ city, lat, lon, zone, note, home }], sights: [...] },
//                                          pins: cities on the Earth in the eyepiece, each
//                                          with its time now (zone: an IANA time zone)
//     launchPad: { countdowns: [{ name, on }], launches, rocket: { name, colors } },
//   }
//
// Everything but version, slug and who is optional; the world fills in a default. Every list and
// every piece of text has a cap (LIMITS), so a spec is small and nothing on a planet runs long.

export const THEMES = ['tiki-night'];
export const PLACES = ['tiki-bar', 'campfire', 'hammock'];
export const DISPLAYS = ['chalkboard', 'star-sky'];
export const SIGHTS = ['moon', 'sun', 'earth'];
// what a thing on someone else's planet is made of: shapes, each a size in metres, a colour and a
// finish. `text` is a flat panel with their own words on it; `lathe` turns a side profile round its
// up (a vase, a bottle, a bun), `extrude` gives a flat outline depth (a guitar, a heart, a cactus),
// and `tube` runs along a path (a handle, a cable, a rail). A part can be repeated in steps (the
// keys of a piano, petals round a stem) and mirrored left to right (a pair of wings).
export const SHAPES = ['box', 'sphere', 'cylinder', 'cone', 'torus', 'capsule', 'text', 'lathe', 'extrude', 'tube'];
export const FINISHES = ['matte', 'gloss', 'metal', 'glass', 'glow'];
// where a thing can stand beside another (near): in front of it (the side it faces), behind it,
// or to its left or right as it faces
export const SIDES = ['front', 'back', 'left', 'right'];
// what a thing does when you walk up to it: sit on it (facing the way it faces) or switch its light
export const USES = ['sit', 'switch', 'greet', 'look', 'change'];
export const SURFACES = ['wood', 'bamboo', 'thatch', 'cloth'];
// Stock things, as a film set keeps stock props: small things every place uses, each a real model
// made once in the house style (tools/planet-models.mjs makes them as the planet "stock"; Steve
// approved the "kit", Oct 27, 2026), so any planet can have one free and at once. A thing with
// stock needs no parts (recognizable parts stand in until the model loads) and no model words; size
// is [width, height, depth] in metres, before scale.
export const STOCK = {
  bench: { size: [1.4, 0.85, 0.55], model: 'A wooden garden bench with a slatted seat and a slatted back, painted pale green, on curled black iron legs and armrests' },
  chair: { size: [0.55, 0.9, 0.55], model: 'A wooden kitchen chair with a rush seat and a ladder back, painted soft blue, a little worn at the edges' },
  table: { size: [1.2, 0.75, 0.8], model: 'A small wooden table with a scrubbed plank top and four sturdy turned legs' },
  'lamp-post': { size: [0.5, 2.6, 0.5], model: 'An old black iron street lamp on a slim post, a four-sided glass lantern on top with a warm glowing bulb inside' },
  crate: { size: [0.7, 0.5, 0.5], model: 'An empty wooden crate with slatted sides and rope handles' },
  sack: { size: [0.5, 0.75, 0.4], model: 'A plump burlap sack tied at the top with string, standing upright' },
  mailbox: { size: [0.45, 1.3, 0.4], model: 'A rounded rural mailbox on a wooden post, painted red, its little flag up' },
  planter: { size: [0.8, 0.8, 0.8], model: 'A round terracotta pot with a leafy green bush in it, dotted with small yellow flowers' },
  fence: { size: [2, 1, 0.15], model: 'A short straight run of white wooden picket fence, three posts with pointed pickets between them' },
  ...LIBRARY_STOCK,
};

export const MIN_PART_SIZE = 0.01;
export const LIMITS = {
  bytes: 64 * 1024, // the whole spec, as JSON
  text: 600, // any one piece of text
  name: 40, // names, labels, titles, cities
  sign: 12,
  links: 8,
  collections: 12,
  items: 200, // in one collection
  depth: 4, // how deep an item's own objects go
  places: 8,
  lines: 30, // the robot's lines, and what to ask it
  pins: 8,
  countdowns: 3,
  things: 24,
  parts: 40, // in one thing
  allParts: 400, // in all of them
  lights: 8, // things that cast light
  size: 12, // metres: the most any part, or a thing's reach from its foot, measures
  points: 32, // in a lathe's profile or an extrude's outline
  path: 16, // in a tube's path
  repeat: 24, // copies of a part made by repeat
};

const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])$/;
const ID = /^[a-z][a-z0-9-]{0,31}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const COLOR = /^#[0-9a-f]{6}$/i;
const isZone = (z) => {
  if (typeof z !== 'string' || !z) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: z }); return true; } catch { return false; }
};

// The telescope's Earth when a planet pins no cities of its own: a few round the world
export const WORLD_PINS = [
  { city: 'Honolulu', lat: 21.31, lon: -157.86, zone: 'Pacific/Honolulu' },
  { city: 'London', lat: 51.51, lon: -0.13, zone: 'Europe/London' },
  { city: 'Tokyo', lat: 35.68, lon: 139.69, zone: 'Asia/Tokyo' },
  { city: 'Sydney', lat: -33.87, lon: 151.21, zone: 'Australia/Sydney' },
  { city: 'Cape Town', lat: -33.92, lon: 18.42, zone: 'Africa/Johannesburg' },
  { city: 'Rio', lat: -22.91, lon: -43.17, zone: 'America/Sao_Paulo' },
];

/** What's wrong with a spec, as a list of "where: what" lines; empty when it's fine. */
export function checkPlanet(spec) {
  const errors = [];
  const fail = (at, what) => errors.push(`${at}: ${what}`);
  const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
  const only = (at, o, keys) => Object.keys(o).forEach((k) => keys.includes(k) || fail(`${at}.${k}`, 'not part of a planet spec'));
  const text = (at, v, max = LIMITS.text, required = false) => {
    if (v === undefined || v === null) return required ? fail(at, 'missing') : undefined;
    if (typeof v !== 'string') return fail(at, 'should be text');
    if (required && !v.trim()) return fail(at, 'empty');
    if (v.length > max) fail(at, `longer than ${max} characters`);
  };
  const list = (at, v, max) => {
    if (v === undefined) return [];
    if (!Array.isArray(v)) return fail(at, 'should be a list'), [];
    if (v.length > max) fail(at, `more than ${max}`);
    return v;
  };
  const color = (at, v) => v === undefined || (typeof v === 'string' && COLOR.test(v)) || fail(at, 'should be a colour like #1a2b3c');
  const between = (at, v, lo, hi) => v === undefined || (typeof v === 'number' && v >= lo && v <= hi) || fail(at, `should be a number from ${lo} to ${hi}`);
  const triple = (at, v, lo, hi) => {
    if (v === undefined) return;
    if (!Array.isArray(v) || v.length !== 3 || !v.every((n) => typeof n === 'number' && n >= lo && n <= hi)) fail(at, `should be [x, y, z], each from ${lo} to ${hi}`);
  };
  // a list of [x, y] or [x, y, z] points, each number from lo to hi
  const points = (at, v, n, lo, hi, least, most) => {
    if (v === undefined) return fail(at, 'missing');
    if (!Array.isArray(v) || v.length < least || v.length > most || !v.every((q) => Array.isArray(q) && q.length === n && q.every((x) => typeof x === 'number' && x >= lo && x <= hi))) {
      fail(at, `should be ${least} to ${most} points, each [${n === 2 ? 'x, y' : 'x, y, z'}] from ${lo} to ${hi}`);
    }
  };
  const id = (at, v) => (typeof v === 'string' && ID.test(v) ? true : (fail(at, 'should be a short id: a-z, 0-9 and -'), false));
  const href = (at, v) => {
    text(at, v, LIMITS.text, true);
    if (typeof v === 'string' && !/^(https:\/\/|mailto:|\/(?!\/))/.test(v)) fail(at, 'should start with https://, mailto: or /');
  };
  // an item in a collection: text, a number, true or false, or a small object or list of them
  const item = (at, v, depth = 0) => {
    if (typeof v === 'string') return text(at, v);
    if (typeof v === 'number' || typeof v === 'boolean') return;
    if (depth >= LIMITS.depth) return fail(at, `nested more than ${LIMITS.depth} deep`);
    if (Array.isArray(v)) return list(at, v, LIMITS.items).forEach((x, i) => item(`${at}[${i}]`, x, depth + 1));
    if (!isObj(v)) return fail(at, 'should be text, a number, true or false, a list or an object');
    for (const [k, x] of Object.entries(v)) {
      if (k.length > LIMITS.name) fail(`${at}`, `has a key longer than ${LIMITS.name} characters`);
      item(`${at}.${k}`, x, depth + 1);
    }
  };

  if (!isObj(spec)) return ['spec: should be an object'];
  only('spec', spec, ['version', 'slug', 'who', 'links', 'look', 'collections', 'things', 'places', 'robot', 'telescope', 'launchPad', 'scenes', 'setting', 'routes', 'arrival', 'reveal']);
  if (JSON.stringify(spec).length > LIMITS.bytes) fail('spec', `larger than ${LIMITS.bytes / 1024} KB`);
  if (spec.arrival !== undefined && spec.arrival !== 'overview') fail('arrival', 'should be overview');
  if (spec.version !== 1) fail('version', 'should be 1');
  if (typeof spec.slug !== 'string' || !SLUG.test(spec.slug)) fail('slug', 'should be 2 to 32 of a-z, 0-9 and -, not starting or ending with -');

  if (!isObj(spec.who)) fail('who', 'missing');
  else {
    only('who', spec.who, ['name', 'sign']);
    text('who.name', spec.who.name, LIMITS.name, true);
    text('who.sign', spec.who.sign, LIMITS.sign);
  }

  const linkIds = new Set();
  list('links', spec.links, LIMITS.links).forEach((l, i) => {
    const at = `links[${i}]`;
    if (!isObj(l)) return fail(at, 'should be an object');
    only(at, l, ['id', 'label', 'note', 'href']);
    if (id(`${at}.id`, l.id) && linkIds.has(l.id)) fail(`${at}.id`, `"${l.id}" is used twice`);
    linkIds.add(l.id);
    text(`${at}.label`, l.label, LIMITS.name, true);
    text(`${at}.note`, l.note, LIMITS.text);
    href(`${at}.href`, l.href);
  });

  if (spec.look !== undefined) {
    if (!isObj(spec.look)) fail('look', 'should be an object');
    else {
      only('look', spec.look, ['theme', 'home', 'ground', 'sky']);
      if (spec.look.theme !== undefined && !THEMES.includes(spec.look.theme)) fail('look.theme', `should be one of ${THEMES.join(', ')}`);
      text('look.home', spec.look.home, LIMITS.name);
      const g = spec.look.ground, sky = spec.look.sky;
      if (g !== undefined) {
        if (!isObj(g)) fail('look.ground', 'should be an object');
        else { only('look.ground', g, ['color', 'hills', 'coast', 'cover']); color('look.ground.color', g.color); between('look.ground.hills', g.hills, 0, 1);
          if(g.cover!==undefined && !['grass','sand','snow','paving'].includes(g.cover))fail('look.ground.cover','should be grass, sand, snow or paving');
          if (g.coast !== undefined) {
            if (!isObj(g.coast)) fail('look.ground.coast', 'should be an object');
            else {
              only('look.ground.coast', g.coast, ['bearing', 'distance', 'radius', 'color']);
              for (const [k,lo,hi] of [['bearing',-180,180],['distance',2,30],['radius',1,8]]) {
                if (g.coast[k] === undefined) fail(`look.ground.coast.${k}`, 'missing');
                between(`look.ground.coast.${k}`,g.coast[k],lo,hi);
              }
              color('look.ground.coast.color',g.coast.color);
            }
          }
        }
      }
      if (sky !== undefined) {
        if (!isObj(sky)) fail('look.sky', 'should be an object');
        else { only('look.sky', sky, ['color', 'light', 'period', 'clouds', 'weather']); if(sky.weather!==undefined && !['clear','rain'].includes(sky.weather))fail('look.sky.weather','should be clear or rain');
          color('look.sky.color', sky.color); color('look.sky.light', sky.light);
          if(sky.period!==undefined && !['day','dawn','dusk','night'].includes(sky.period)) fail('look.sky.period','should be day, dawn, dusk or night');
          if(sky.clouds!==undefined && !['clear','overcast'].includes(sky.clouds)) fail('look.sky.clouds','should be clear or overcast'); }
      }
    }
  }

  const collections = spec.collections === undefined ? {} : spec.collections;
  if (!isObj(collections)) fail('collections', 'should be an object');
  else {
    if (Object.keys(collections).length > LIMITS.collections) fail('collections', `more than ${LIMITS.collections}`);
    for (const [k, c] of Object.entries(collections)) {
      const at = `collections.${k}`;
      id(at, k);
      if (!isObj(c)) { fail(at, 'should be an object'); continue; }
      only(at, c, ['title', 'items', 'src']);
      text(`${at}.title`, c.title, LIMITS.name, true);
      if ((c.items === undefined) === (c.src === undefined)) fail(at, 'should have items or src, not both');
      if (c.src !== undefined && !(typeof c.src === 'string' && /^\/(?!\/)\S*$/.test(c.src))) fail(`${at}.src`, 'should be a site path');
      list(`${at}.items`, c.items, LIMITS.items).forEach((x, i) => item(`${at}.items[${i}]`, x));
    }
  }
  const known = (at, c) => c === undefined || (isObj(collections) && collections[c]) || fail(at, `no collection "${c}"`);

  const thingIds = new Set();
  let parts = 0, lights = 0;
  list('things', spec.things, LIMITS.things).forEach((t, i) => {
    const at = `things[${i}]`;
    if (!isObj(t)) return fail(at, 'should be an object');
    only(at, t, ['id', 'name', 'note', 'collection', 'at', 'near', 'on', 'turn', 'scale', 'solid', 'model', 'stock', 'use', 'parts', 'light', 'footprint', 'craft', 'role', 'seatView', 'frameWith', 'response']);
    if(t.frameWith!==undefined && (typeof t.frameWith!=='string'||!thingIds.has(t.frameWith)))fail(`${at}.frameWith`,'should name a thing listed before this one');
    if (t.craft !== undefined && !['palm', 'cat', 'planting', 'pavilion', 'birch', 'woodland-floor', 'bird', 'easel', 'canopy'].includes(t.craft)) fail(`${at}.craft`, 'unknown authored craft');
    if (id(`${at}.id`, t.id) && thingIds.has(t.id)) fail(`${at}.id`, `"${t.id}" is used twice`);
    thingIds.add(t.id);
    text(`${at}.name`, t.name, LIMITS.name, true);
    text(`${at}.note`, t.note, LIMITS.text);
    text(`${at}.model`, t.model, LIMITS.text);
    if (t.footprint !== undefined && (!Array.isArray(t.footprint) || t.footprint.length !== 2 || !t.footprint.every((n) => typeof n === 'number' && n >= 0.05 && n <= 12))) fail(`${at}.footprint`, 'should be [width, depth], each 0.05 to 12 metres');
    known(`${at}.collection`, t.collection);
    // placed from where you land (at), or beside another thing placed before it (near)
    if ([t.at,t.near,t.on].filter(v=>v!==undefined).length!==1) fail(`${at}.at`, 'choose exactly one of at, near or on');
    else if(t.on!==undefined) {
      if(!isObj(t.on))fail(`${at}.on`,'should be { of, at: [x, z] }');
      else {
        only(`${at}.on`,t.on,['of','at']);
        const anchor=spec.things.find(a=>a?.id===t.on.of);
        if(!thingIds.has(t.on.of)||t.on.of===t.id||!STOCK[anchor?.stock]?.support||anchor.on)fail(`${at}.on.of`,'needs an earlier ground-level stock side-table with a declared flat support');
        if(!Array.isArray(t.on.at)||t.on.at.length!==2||!t.on.at.every(n=>typeof n==='number'&&Number.isFinite(n)))fail(`${at}.on.at`,'needs [x, z] in metres on the tabletop');
      }
      if(t.solid!==false||t.footprint||t.craft||(Array.isArray(t.parts)&&t.parts.some(p=>p?.ground))||t.use&&t.use!=='switch')fail(`${at}.on`,'tabletop props need solid: false, no ground feet/footprint/craft, and only an optional switch action');
    }
    else if (t.at !== undefined) {
      if (!isObj(t.at)) fail(`${at}.at`, 'should be { bearing, distance }');
      else {
        only(`${at}.at`, t.at, ['bearing', 'distance']);
        if (t.at.bearing === undefined) fail(`${at}.at.bearing`, 'missing');
        between(`${at}.at.bearing`, t.at.bearing, -180, 180);
        if (t.at.distance === undefined) fail(`${at}.at.distance`, 'missing');
        between(`${at}.at.distance`, t.at.distance, 2, 60);
      }
    } else if (!isObj(t.near)) fail(`${at}.near`, 'should be { of, side, gap }');
    else {
      only(`${at}.near`, t.near, ['of', 'side', 'gap']);
      if (!thingIds.has(t.near.of) || t.near.of === t.id) fail(`${at}.near.of`, 'should be the id of a thing listed before this one');
      if (!SIDES.includes(t.near.side)) fail(`${at}.near.side`, `should be one of ${SIDES.join(', ')}`);
      between(`${at}.near.gap`, t.near.gap, 0, 10);
    }
    between(`${at}.turn`, t.turn, -180, 180);
    between(`${at}.scale`, t.scale, 0.25, 4);
    if (t.solid !== undefined && typeof t.solid !== 'boolean') fail(`${at}.solid`, 'should be true or false');
    if(t.seatView!==undefined) {
      if(!isObj(t.seatView)||t.use!=='sit')fail(`${at}.seatView`,'needs a sit action and an object');
      else {
        only(`${at}.seatView`,t.seatView,['eye','pitch','toward']);between(`${at}.seatView.eye`,t.seatView.eye,.8,1.6);between(`${at}.seatView.pitch`,t.seatView.pitch,-.5,.3);
        if(t.seatView.toward!==undefined&&(typeof t.seatView.toward!=='string'||!t.seatView.toward||t.seatView.pitch!==undefined))fail(`${at}.seatView.toward`,'name an activity target instead of an absolute pitch');
      }
    }
    if(t.role!==undefined && !['focus','support','environment'].includes(t.role)) fail(`${at}.role`,'should be focus, support or environment');
    if (t.use !== undefined && !USES.includes(t.use)) fail(`${at}.use`, `should be one of ${USES.join(', ')}`);
    else if (t.use === 'switch' && t.light === undefined) fail(`${at}.use`, 'switch turns its light on and off, so it needs a light');
    if (t.use === 'greet' && !['cat','bird'].includes(t.craft)) fail(`${at}.use`, 'greet needs an authored cat or bird');
    if (t.craft && (t.model || t.stock)) fail(`${at}.craft`, 'authored craft cannot be replaced by a static model');
    if (t.stock !== undefined && !Object.hasOwn(STOCK, t.stock)) fail(`${at}.stock`, `should be one of ${Object.keys(STOCK).join(', ')}`);
    else if (t.stock !== undefined && t.model !== undefined) fail(`${at}.model`, 'a stock thing has its model already; give it model words or stock, not both');
    const ps = list(`${at}.parts`, t.parts, LIMITS.parts);
    if(STOCK[t.stock]?.file && t.parts!==undefined)fail(`${at}.parts`,'ready library stock uses its measured envelope; omit parts');
    if (!ps.length && t.stock === undefined) fail(`${at}.parts`, 'should have at least one part');
    ps.forEach((p, j) => {
      const pat = `${at}.parts[${j}]`;
      if (!isObj(p)) return fail(pat, 'should be an object');
      only(pat, p, ['shape', 'size', 'at', 'turn', 'color', 'finish', 'spin', 'text', 'round', 'profile', 'outline', 'path', 'thick', 'repeat', 'mirror', 'surface', 'joint', 'ground', 'after']);
      if(p.after!==undefined){
        if(t.use!=='change'||!isObj(p.after)||p.repeat||p.mirror||p.joint||p.spin||p.ground||!['box','sphere','cylinder','cone','capsule'].includes(p.shape))fail(`${pat}.after`,'needs a change action and one unanimated basic part without ground/repeat/mirror/joints');
        else {
          only(`${pat}.after`,p.after,['size','at']);
          if(!Object.keys(p.after).length)fail(`${pat}.after`,'needs a new size or position');
          triple(`${pat}.after.size`,p.after.size,MIN_PART_SIZE,LIMITS.size);
          triple(`${pat}.after.at`,p.after.at,-LIMITS.size,LIMITS.size);
        }
      }
      if(p.ground!==undefined && typeof p.ground!=='boolean')fail(`${pat}.ground`,'should be true or false');
      if(p.ground && (!['box','cylinder'].includes(p.shape) || p.repeat || p.mirror || p.spin || p.joint || p.turn?.[0] || p.turn?.[2]))fail(`${pat}.ground`,'only an upright, unanimated box or cylinder can meet the ground');
      if (p.surface !== undefined && !SURFACES.includes(p.surface)) fail(`${pat}.surface`, `should be one of ${SURFACES.join(', ')}`);
      if (p.joint !== undefined && (!['cat','bird'].includes(t.craft) || !['head', 'tail'].includes(p.joint))) fail(`${pat}.joint`, 'only an authored character has head or tail joints');
      if (p.surface && (p.shape === 'text' || p.finish && p.finish !== 'matte')) fail(`${pat}.surface`, 'a surface needs an opaque matte part');
      // a repeated or mirrored part counts once for each copy it makes
      const copies = (isObj(p.repeat) && Number.isInteger(p.repeat.count) ? p.repeat.count : 1) * (p.mirror === true ? 2 : 1);
      parts += copies;
      if (!SHAPES.includes(p.shape)) fail(`${pat}.shape`, `should be one of ${SHAPES.join(', ')}`);
      if (p.size === undefined) fail(`${pat}.size`, 'missing: [width, height, depth] in metres');
      triple(`${pat}.size`, p.size, MIN_PART_SIZE, LIMITS.size);
      triple(`${pat}.at`, p.at, -LIMITS.size, LIMITS.size);
      triple(`${pat}.turn`, p.turn, -360, 360);
      color(`${pat}.color`, p.color);
      if (p.finish !== undefined && !FINISHES.includes(p.finish)) fail(`${pat}.finish`, `should be one of ${FINISHES.join(', ')}`);
      between(`${pat}.spin`, p.spin, -360, 360);
      if (p.shape === 'text') text(`${pat}.text`, p.text, LIMITS.name * 2, true);
      else if (p.text !== undefined) fail(`${pat}.text`, 'only a text part has text');
      // each shape's own keys, and only on that shape
      const own = { round: 'box', profile: 'lathe', outline: 'extrude', path: 'tube', thick: 'tube' };
      for (const [k, shape] of Object.entries(own)) if (p[k] !== undefined && p.shape !== shape) fail(`${pat}.${k}`, `only a ${shape} part has ${k}`);
      between(`${pat}.round`, p.round, 0, 0.5);
      if (p.shape === 'lathe') points(`${pat}.profile`, p.profile, 2, 0, 1, 2, LIMITS.points);
      if (p.shape === 'extrude') points(`${pat}.outline`, p.outline, 2, 0, 1, 3, LIMITS.points);
      if (p.shape === 'tube') { points(`${pat}.path`, p.path, 3, -0.5, 0.5, 2, LIMITS.path); between(`${pat}.thick`, p.thick, 0.005, 2); }
      if (p.repeat !== undefined) {
        if (!isObj(p.repeat)) fail(`${pat}.repeat`, 'should be { count, at, turn }');
        else {
          only(`${pat}.repeat`, p.repeat, ['count', 'at', 'turn']);
          if (!Number.isInteger(p.repeat.count) || p.repeat.count < 2 || p.repeat.count > LIMITS.repeat) fail(`${pat}.repeat.count`, `should be a whole number from 2 to ${LIMITS.repeat}`);
          triple(`${pat}.repeat.at`, p.repeat.at, -LIMITS.size, LIMITS.size);
          triple(`${pat}.repeat.turn`, p.repeat.turn, -360, 360);
        }
      }
      if (p.mirror !== undefined && typeof p.mirror !== 'boolean') fail(`${pat}.mirror`, 'should be true or false');
    });
    if(t.use==='change' || t.response!==undefined){
      if(t.use!=='change'||!isObj(t.response)||t.model||t.stock||t.craft||t.on)fail(`${at}.response`,'change needs custom ground-level parts and response labels; no model/stock/craft/tabletop');
      else {only(`${at}.response`,t.response,['label','done']);text(`${at}.response.label`,t.response.label,LIMITS.name,true);text(`${at}.response.done`,t.response.done,LIMITS.name,true);}
      if(!ps.some(p=>p?.after))fail(`${at}.response`,'change needs at least one part with after');
      if(ps.filter(p=>p?.after).length>8)fail(`${at}.response`,'at most eight changing parts');
    }
    if (t.light !== undefined) {
      lights++;
      if (!isObj(t.light)) fail(`${at}.light`, 'should be an object');
      else {
        only(`${at}.light`, t.light, ['color', 'strength', 'at']);
        color(`${at}.light.color`, t.light.color);
        between(`${at}.light.strength`, t.light.strength, 0, 1);
        triple(`${at}.light.at`, t.light.at, -LIMITS.size, LIMITS.size);
      }
    }
  });
  if (parts > LIMITS.allParts) fail('things', `more than ${LIMITS.allParts} parts in all`);
  if (lights > LIMITS.lights) fail('things', `more than ${LIMITS.lights} with a light`);
  // Bounds are measured only after shape validation, so malformed output is a diagnostic.
  if(!errors.length) {
    const mounted=[];
    for(const [i,t] of (spec.things||[]).entries()) {
      if(t.seatView?.toward && (t.seatView.toward===t.id||!thingIds.has(t.seatView.toward)))fail(`things[${i}].seatView.toward`,'should name another thing in this planet');
      if(t.near && spec.things.find(a=>a.id===t.near.of)?.on)fail(`things[${i}].near.of`,'ground placement needs a ground anchor, not a tabletop prop');
      if(!t.on)continue;
      const anchor=spec.things.find(a=>a.id===t.on.of),support=STOCK[anchor.stock].support,bounds=tabletopBounds(t);
      if(Math.abs(boxOf(partBoxes(t))[2])>.02)fail(`things[${i}].on`,'the prop foot must be at local y=0 to meet the tabletop');
      if(bounds.some((v,k)=>Math.abs(v)>support.half[k<2?0:1]+.0001))fail(`things[${i}].on`,'the whole prop must fit inside the declared tabletop');
      if(mounted.some(m=>m.of===t.on.of&&bounds[0]<m.b[1]-.003&&bounds[1]>m.b[0]+.003&&bounds[2]<m.b[3]-.003&&bounds[3]>m.b[2]+.003))fail(`things[${i}].on`,'tabletop props overlap; separate their on.at offsets');
      mounted.push({of:t.on.of,b:bounds});
    }
  }

  const placeIds = new Set();
  list('places', spec.places, LIMITS.places).forEach((p, i) => {
    const at = `places[${i}]`;
    if (!isObj(p)) return fail(at, 'should be an object');
    only(at, p, ['id', 'kind', 'displays', 'collection']);
    if (id(`${at}.id`, p.id) && placeIds.has(p.id)) fail(`${at}.id`, `"${p.id}" is used twice`);
    placeIds.add(p.id);
    if (!PLACES.includes(p.kind)) fail(`${at}.kind`, `should be one of ${PLACES.join(', ')}`);
    known(`${at}.collection`, p.collection);
    list(`${at}.displays`, p.displays, LIMITS.places).forEach((d, j) => {
      const dat = `${at}.displays[${j}]`;
      if (!isObj(d)) return fail(dat, 'should be an object');
      only(dat, d, ['kind', 'collection']);
      if (!DISPLAYS.includes(d.kind)) fail(`${dat}.kind`, `should be one of ${DISPLAYS.join(', ')}`);
      if (d.collection === undefined) fail(`${dat}.collection`, 'missing');
      else known(`${dat}.collection`, d.collection);
    });
  });

  if (spec.robot !== undefined) {
    if (!isObj(spec.robot)) fail('robot', 'should be an object');
    else {
      only('robot', spec.robot, ['lines', 'ask']);
      list('robot.lines', spec.robot.lines, LIMITS.lines).forEach((l, i) => text(`robot.lines[${i}]`, l, LIMITS.text, true));
      list('robot.ask', spec.robot.ask, LIMITS.lines).forEach((l, i) => text(`robot.ask[${i}]`, l, LIMITS.name * 2, true));
    }
  }

  if (spec.telescope !== undefined) {
    const t = spec.telescope;
    if (!isObj(t)) fail('telescope', 'should be an object');
    else {
      only('telescope', t, ['engraving', 'pins', 'sights']);
      text('telescope.engraving', t.engraving, LIMITS.sign);
      list('telescope.pins', t.pins, LIMITS.pins).forEach((p, i) => {
        const at = `telescope.pins[${i}]`;
        if (!isObj(p)) return fail(at, 'should be an object');
        only(at, p, ['city', 'lat', 'lon', 'zone', 'note', 'home']);
        text(`${at}.city`, p.city, LIMITS.name, true);
        if (typeof p.lat !== 'number' || !(Math.abs(p.lat) <= 90)) fail(`${at}.lat`, 'should be a latitude, -90 to 90');
        if (typeof p.lon !== 'number' || !(Math.abs(p.lon) <= 180)) fail(`${at}.lon`, 'should be a longitude, -180 to 180');
        if (!isZone(p.zone)) fail(`${at}.zone`, 'should be a time zone, like America/Chicago');
        text(`${at}.note`, p.note, LIMITS.name * 2);
        if (p.home !== undefined && typeof p.home !== 'boolean') fail(`${at}.home`, 'should be true or false');
      });
      list('telescope.sights', t.sights, SIGHTS.length).forEach((s, i) => SIGHTS.includes(s) || fail(`telescope.sights[${i}]`, `should be one of ${SIGHTS.join(', ')}`));
    }
  }

  if (spec.launchPad !== undefined) {
    const l = spec.launchPad;
    if (!isObj(l)) fail('launchPad', 'should be an object');
    else {
      only('launchPad', l, ['countdowns', 'launches', 'rocket']);
      list('launchPad.countdowns', l.countdowns, LIMITS.countdowns).forEach((c, i) => {
        const at = `launchPad.countdowns[${i}]`;
        if (!isObj(c)) return fail(at, 'should be an object');
        only(at, c, ['name', 'on']);
        text(`${at}.name`, c.name, LIMITS.name, true);
        if (typeof c.on !== 'string' || !DATE.test(c.on) || new Date(`${c.on}T12:00:00Z`).toISOString().slice(0, 10) !== c.on) fail(`${at}.on`, 'should be a date, YYYY-MM-DD');
      });
      // whose real launches the console shows: everyone's, nobody's, or one company or agency's
      if (l.launches !== undefined && l.launches !== 'all' && l.launches !== 'none') {
        if (!isObj(l.launches)) fail('launchPad.launches', 'should be "all", "none" or { provider }');
        else { only('launchPad.launches', l.launches, ['provider']); text('launchPad.launches.provider', l.launches.provider, LIMITS.name, true); }
      }
      if (l.rocket !== undefined) {
        if (!isObj(l.rocket)) fail('launchPad.rocket', 'should be an object');
        else {
          only('launchPad.rocket', l.rocket, ['name', 'colors']);
          text('launchPad.rocket.name', l.rocket.name, LIMITS.sign);
          list('launchPad.rocket.colors', l.rocket.colors, 2).forEach((c, i) => (typeof c === 'string' && COLOR.test(c)) || fail(`launchPad.rocket.colors[${i}]`, 'should be a colour like #1a2b3c'));
        }
      }
    }
  }

  list('routes', spec.routes, 3).forEach((r, i) => {
    const at = `routes[${i}]`;
    if (!isObj(r)) return fail(at, 'should be an object');
    only(at,r,['from','to']);
    for (const k of ['from','to']) {
      if (!isObj(r[k])) { fail(`${at}.${k}`, 'should be { bearing, distance }'); continue; }
      only(`${at}.${k}`,r[k],['bearing','distance']);
      if (r[k].bearing === undefined || r[k].distance === undefined) fail(`${at}.${k}`, 'missing bearing or distance');
      between(`${at}.${k}.bearing`,r[k].bearing,-180,180);
      between(`${at}.${k}.distance`,r[k].distance,0,60);
    }
  });
  errors.push(...sceneErrors(spec));
  errors.push(...settingErrors(spec));
  if(!errors.length && spec.reveal!==undefined && (typeof spec.reveal!=='string'||!composePlanet(spec).things?.some(t=>t.id===spec.reveal)))fail('reveal','should name a thing to feature in an overview arrival');
  // Generated geometry has the same caps as handwritten geometry; raw scene input cannot
  // bypass the 24-thing / 400-part / 64 KB limits.
  if (!errors.length && (spec.scenes||spec.setting)) {
    const compiled = composePlanet(spec), ids = compiled.things.map((t)=>t.id);
    if (new Set(ids).size !== ids.length) fail(spec.setting?'setting':'scenes','a generated id collides with a custom thing; change the setting anchor or scene id');
    else errors.push(...checkPlanet(compiled));
  }
  return errors;
}

// ---------- What the world draws ----------
/**
 * What's on a planet besides the planet itself, from a spec that passed checkPlanet: the kinds of
 * the places it lists, and 'telescope' and 'launch-pad' when it has them. Nothing else is drawn
 * on someone else's planet (assets/js/world/places.js layout); Steve's has everything, his own
 * things too.
 */
export function planetHas(spec) {
  const kinds = new Set((spec.places || []).map((p) => p.kind));
  if (spec.telescope) kinds.add('telescope');
  if (spec.launchPad) kinds.add('launch-pad');
  return kinds;
}

// an item in a collection as one line: text as it is; an object by its name or title, or its first text
export const line = (x) => {
  if (x === null || x === undefined) return '';
  if (typeof x !== 'object') return String(x);
  if (Array.isArray(x)) return x.map(line).filter(Boolean).join(', ');
  return x.name || x.title || x.label || Object.values(x).find((v) => typeof v === 'string') || '';
};

/**
 * What the world's places show, from a spec that passed checkPlanet (read by the world, and by the
 * chat's card where 3D can't run): the sign's name, the chalkboard (the collection a tiki bar's
 * chalkboard display points at), the campfire's thoughts (a campfire's collection) and the
 * robot's lines. Anything the spec leaves out gets a plain default.
 */
export function planetParts(spec) {
  const places = spec.places || [], collections = spec.collections || {};
  const shown = (kind, display) => {
    for (const p of places.filter((x) => x.kind === kind)) {
      const id = display ? ((p.displays || []).find((d) => d.kind === display) || {}).collection : p.collection;
      if (collections[id]) return collections[id];
    }
    return null;
  };
  const board = shown('tiki-bar', 'chalkboard'), fire = shown('campfire');
  const items = ((board && board.items) || []).map((x) => (x && typeof x === 'object' && !Array.isArray(x) ? { ...x, name: line(x) } : { name: line(x) })).filter((x) => x.name);
  const robot = spec.robot || {};
  return {
    name: spec.who.name,
    sign: spec.who.sign || spec.who.name,
    engraving: ((spec.telescope && spec.telescope.engraving) || spec.who.sign || spec.who.name).toUpperCase(),
    // a drink has a recipe (build) and the robot can make it (make); anything else is a name and its details
    board: { title: (board && board.title) || 'Tonight', items, recipes: items.some((x) => Array.isArray(x.build)) },
    thoughts: ((fire && fire.items) || []).map(line).filter(Boolean),
    robot: { lines: robot.lines && robot.lines.length ? robot.lines : ['Welcome in. Have a look around.'], ask: robot.ask || [] },
    pins: (spec.telescope && spec.telescope.pins && spec.telescope.pins.length ? spec.telescope.pins : WORLD_PINS),
    has: [...planetHas(spec)],
  };
}

// ---------- Looking it over ----------
// What a careful eye would say about a planet's things before anyone walks round it: a part
// hanging in the air, two things in each other, something too small to see or too long for a
// planet this round, a planet with nothing lit. Boxes are measured as if nothing were turned, so
// these are things to look at, not errors; the preview tells the AI who made it (preview_planet).
const GROUND_RADIUS = 20; // world/planet.js RADIUS: the ground's distance from the middle, in metres

/** A thing's parts: its own, or for a stock thing without any, a box its size that stands in for its model. */
export function partsOf(t) {
  if (t.parts && t.parts.length) return t.parts;
  const k = t.stock && STOCK[t.stock];
  if(k?.file)return [{shape:'box',size:k.size,at:[0,k.size[1]/2,0],color:'#b8b1a2'}]; // layout envelope, never revealed
  return k ? stockParts(t.stock,k.size) : [];
}

function tabletopBounds(t) {
  const b=boxOf(partBoxes(t)),a=(t.turn||0)*Math.PI/180,xs=[],zs=[];
  for(const x of [b[0],b[1]])for(const z of [b[4],b[5]]){xs.push(t.on.at[0]+x*Math.cos(a)+z*Math.sin(a));zs.push(t.on.at[1]-x*Math.sin(a)+z*Math.cos(a));}
  return [Math.min(...xs),Math.max(...xs),Math.min(...zs),Math.max(...zs)];
}

// what a friend's planet can't draw: Steve's own planet's parts, which pass checkPlanet for his spec
const STEVES_PARTS = ['places', 'robot', 'telescope', 'launchPad'];
const DEG = Math.PI / 180;
// in full view from where you land: the horizon is about 6 m off, a thing rises over it by 10 m
export const IN_VIEW = 10;
// the first view's half-width, in degrees either side of straight ahead (bearing 0), on the
// narrowest screen it opens on: a phone held upright (assets/js/planets/main.js: 72° tall at
// 390x844 is 18.6° either side), less a margin, so a thing shows more than its edge
export const FIRST_VIEW = 15;

/** Steve's planet's parts with something in them, which a friend's planet doesn't draw. */
export function notDrawn(spec) {
  const filled = (v) => v !== undefined && v !== null && v !== false && !(Array.isArray(v) && !v.length) && !(typeof v === 'object' && !Array.isArray(v) && !Object.keys(v).length);
  return STEVES_PARTS.filter((k) => filled(spec[k])).map((k) => `${k}: only Steve's own planet has these, and a friend's planet doesn't draw them; make what's here into things instead (get_kit, things)`);
}

// how far a thing reaches either side of its foot, in metres (its parts, unturned)
const halfWidth = (t) => (t.scale || 1) * Math.max(0.1, ...partsOf(t).map((p) => {
  const [x = 0, , z = 0] = p.at || [], [w = 0, , d = 0] = p.size || [];
  return Math.max(Math.abs(x) + w / 2, Math.abs(z) + d / 2);
}));

/**
 * Why a friend's planet, from a spec that passed checkPlanet, would show nothing when they land
 * on it: each "where: what", none when something is in the first view. Something is when a thing
 * stands within IN_VIEW metres and reaches to within FIRST_VIEW degrees of straight ahead. The
 * connector won't preview or send such a planet, so their AI can't show them bare rock and call
 * it done; Steve's planet's parts are named then too, as the likely reason.
 */
export function showsNothing(spec) {
  const why = [];
  const things = spec.things || [];
  if (!things.length) why.push("things: none, so they'd land on bare rock; make them a few things from what they love (get_kit, things)");
  else {
    const placed = placeThings(things);
    const at = things.map((t) => ({ t, p: placed.get(t.id) })).filter((x) => x.p);
    const nearest = Math.min(...at.map((x) => x.p.distance));
    const seen = at.some(({ t, p }) => p.distance <= IN_VIEW && Math.abs(p.bearing) - Math.atan2(halfWidth(t), p.distance) / DEG <= FIRST_VIEW);
    if (nearest > IN_VIEW) why.push(`things: the nearest is ${nearest.toFixed(0)} m from where they land, over the horizon, so they'd land on bare rock; put the first place 3 to ${IN_VIEW} m out`);
    else if (!seen) why.push(`things: none in the first view (straight ahead, about ${FIRST_VIEW}° either side on a phone; everything within ${IN_VIEW} m is behind or beside them), so they'd see bare rock; put the first place nearly straight ahead (bearing -${FIRST_VIEW} to ${FIRST_VIEW})`);
  }
  return why.length ? [...notDrawn(spec), ...why] : why;
}

/** Notes on a spec that passed checkPlanet, each "where: what", for its maker to look at. */
// each part's box in metres from its thing's foot: [x0, x1, y0, y1, z0, z1], copies included
function partBoxes(t,allStates=false) {
  return partsOf(t).flatMap(p=>allStates&&p.after?[p,{...p,...p.after,after:undefined}]:[p]).map((p) => {
    // a turned part is measured as if it could face any way: turned about its up, its longest side
    // either way along the ground; tipped over, its longest side every way
    const [x, y, z] = p.size, [tx, ty, tz] = (p.turn || [0, 0, 0]).map((a) => a % 180 !== 0), s = t.scale || 1;
    const [w, h, d] = tx || tz ? Array(3).fill(Math.max(x, y, z)) : ty ? [Math.max(x, z), y, Math.max(x, z)] : p.size;
    const at = p.at || [0, h / 2, 0];
    const n = p.repeat ? p.repeat.count : 1, step = (p.repeat && p.repeat.at) || [0, 0, 0];
    const xs = [], ys = [], zs = [];
    for (let i = 0; i < n; i += Math.max(1, n - 1)) {
      for (const m of p.mirror ? [1, -1] : [1]) {
        const x = (at[0] + step[0] * i) * m, y = at[1] + step[1] * i, z = at[2] + step[2] * i;
        xs.push(x - w / 2, x + w / 2); ys.push(y - h / 2, y + h / 2); zs.push(z - d / 2, z + d / 2);
      }
    }
    return [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys), Math.min(...zs), Math.max(...zs)].map((v) => v * s);
  });
}
const boxOf = (bs) => [Math.min(...bs.map((b) => b[0])), Math.max(...bs.map((b) => b[1])), Math.min(...bs.map((b) => b[2])), Math.max(...bs.map((b) => b[3])), Math.min(...bs.map((b) => b[4])), Math.max(...bs.map((b) => b[5]))];

/** How far a thing reaches from its foot along the ground, in metres. */
export function thingReach(t) {
  const footprint=t.footprint?Math.max(...t.footprint)/2*(t.scale||1):0;
  if (t.footprint && t.use!=='change') return footprint;
  const bs = partBoxes(t,true);
  if (!bs.length) return 0;
  const all = boxOf(bs);
  return Math.max(footprint,Math.abs(all[0]), Math.abs(all[1]), Math.abs(all[4]), Math.abs(all[5]));
}

/**
 * Where each thing stands: { bearing, distance, turn } by id, from where you land. A thing placed
 * with at stands there; one placed near another stands on that side of it, clear of it by gap
 * (half a metre unless said), facing it, then turned by its own turn. Worked out on a flat map of
 * the ground round where you land (x to the right, n ahead), close enough on a planet this size.
 * turn is from facing where you land, as things.js turns them.
 */
export function placeThings(things = []) {
  const placed = new Map(), DEG = Math.PI / 180;
  const rot = ([x, n], a) => [x * Math.cos(a) - n * Math.sin(a), x * Math.sin(a) + n * Math.cos(a)];
  const unit = ([x, n]) => { const l = Math.hypot(x, n) || 1; return [x / l, n / l]; };
  for (const t of things) {
    if (t.at) { placed.set(t.id, { bearing: t.at.bearing, distance: t.at.distance, turn: t.turn || 0 }); continue; }
    if(t.on) {
      const anchor=placed.get(t.on.of);if(!anchor)continue;
      const a=(anchor.bearing-anchor.turn)*DEG,s=things.find(a=>a.id===t.on.of).scale||1,[x,z]=t.on.at.map(n=>n*s);
      const px=anchor.distance*Math.sin(anchor.bearing*DEG)+x*Math.cos(a)-z*Math.sin(a),pn=anchor.distance*Math.cos(anchor.bearing*DEG)-x*Math.sin(a)-z*Math.cos(a),bearing=Math.atan2(px,pn)/DEG;
      placed.set(t.id,{bearing,distance:Math.hypot(px,pn),turn:bearing-anchor.bearing+anchor.turn+(t.turn||0)});continue;
    }
    const of = placed.get(t.near && t.near.of), anchor = things.find((x) => x.id === (t.near && t.near.of));
    if (!of) continue;
    const p = [of.distance * Math.sin(of.bearing * DEG), of.distance * Math.cos(of.bearing * DEG)];
    // the side it faces: back toward where you land, then its own turn
    const front = rot(unit([-p[0], -p[1]]), of.turn * DEG);
    // left and right are beside it and a little forward, so the anchor never hides them from the path
    const side = { front, back: [-front[0], -front[1]], left: rot(front, Math.PI / 3), right: rot(front, -Math.PI / 3) }[t.near.side];
    const apart = thingReach(anchor) + (t.near.gap ?? 0.5) + thingReach(t);
    const c = [p[0] + side[0] * apart, p[1] + side[1] * apart];
    // facing the thing it's near: the turn from facing where you land to facing it
    const from = unit([-c[0], -c[1]]), to = unit([p[0] - c[0], p[1] - c[1]]);
    const face = Math.atan2(from[0] * to[1] - from[1] * to[0], from[0] * to[0] + from[1] * to[1]) / DEG;
    const turn = ((face + (t.turn || 0) + 540) % 360) - 180;
    placed.set(t.id, { bearing: Math.atan2(c[0], c[1]) / DEG, distance: Math.max(0.5, Math.hypot(c[0], c[1])), turn });
  }
  return placed;
}

// Separating axes on the local map: a table's long side is not its depth. Canopies and
// unsupported lights are visual bounds, not solid ground footprints.
function footprintsOverlap(a,b,placed) {
  const rectangle = (t) => {
    const p = placed.get(t.id), angle = (-p.bearing + p.turn) * DEG;
    return {center:[Math.sin(p.bearing*DEG)*p.distance,Math.cos(p.bearing*DEG)*p.distance],axes:[[Math.cos(angle),Math.sin(angle)],[-Math.sin(angle),Math.cos(angle)]],half:t.footprint.map((n)=>n*(t.scale||1)/2)};
  };
  const ra=rectangle(a),rb=rectangle(b),delta=ra.center.map((v,i)=>v-rb.center[i]),dot=(x,y)=>x[0]*y[0]+x[1]*y[1];
  return [...ra.axes,...rb.axes].every((axis)=>Math.abs(dot(delta,axis)) < ra.half.reduce((n,h,i)=>n+h*Math.abs(dot(ra.axes[i],axis)),0)+rb.half.reduce((n,h,i)=>n+h*Math.abs(dot(rb.axes[i],axis)),0)-0.08);
}

export function lookOver(spec) {
  const notes = [];
  const things = spec.things || [];
  const reach = new Map(), placed = placeThings(things);
  const lit = things.some((t) => t.light || partsOf(t).some((p) => p.finish === 'glow'));
  things.forEach((t, i) => {
    const bs = partBoxes(t);
    if (!bs.length) return;
    const all = boxOf(bs);
    const wide = Math.max(all[1] - all[0], all[5] - all[4]), tall = all[3] - all[2];
    if (wide < 0.4 && tall < 0.4 && !t.on) notes.push(`things[${i}] (${t.name}): under 40 cm all round, hard to see from a few steps away; make it bigger (scale)`);
    // the ground falls away by length² / 8R under something straight: 15 cm at 5 m
    // Known trees have grounded trunks; the authored canopy has four terrain-fitted feet.
    if (wide > 5 && !['birch','palm','canopy'].includes(t.craft)) notes.push(`things[${i}] (${t.name}): ${wide.toFixed(1)} m long, so on a planet this round its ends lift off the ground; shorten it or stand it on legs`);
    // a part off the ground that touches no other part: it hangs in the air
    const near = (a, b) => a[0] <= b[1] + 0.02 && a[1] >= b[0] - 0.02 && a[2] <= b[3] + 0.02 && a[3] >= b[2] - 0.02 && a[4] <= b[5] + 0.02 && a[5] >= b[4] - 0.02;
    bs.forEach((b, j) => {
      const p = partsOf(t)[j];
      if (p.shape === 'text' || p.finish === 'glow' || b[2] < 0.05) return;
      const held = bs.some((o, k) => k !== j && near(o, b));
      if (!held) notes.push(`things[${i}].parts[${j}] (${p.shape}): touches nothing and hangs ${b[2].toFixed(2)} m above the ground; move it onto another part or the ground (at is its middle)`);
    });
    reach.set(t, thingReach(t));
  });
  // two things in each other: their feet closer than they reach
  const where = (t) => {
    const at = placed.get(t.id);
    if (!at) return null;
    const b = at.bearing * Math.PI / 180, a = at.distance / GROUND_RADIUS;
    return [Math.sin(a) * Math.sin(b), Math.cos(a), -Math.sin(a) * Math.cos(b)];
  };
  for (let i = 0; i < things.length; i++) {
    for (let j = i + 1; j < things.length; j++) {
      const [a, b] = [where(things[i]), where(things[j])];
      if (!a || !b) continue;
      const apart = Math.acos(Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2])) * GROUND_RADIUS;
      if (things[i].solid === false || things[j].solid === false) continue;
      if (things[i].footprint && things[j].footprint) {
        if (footprintsOverlap(things[i], things[j], placed)) notes.push(`things[${i}] (${things[i].name}) and things[${j}] (${things[j].name}): ground footprints overlap; move one`);
        continue;
      }
      if (apart < (reach.get(things[i]) || 0) + (reach.get(things[j]) || 0) - 0.3) notes.push(`things[${i}] (${things[i].name}) and things[${j}] (${things[j].name}): ${apart.toFixed(1)} m apart, so they stand in each other; move one (its at, or its near's gap)`);
    }
  }
  if (things.length && !lit && spec.look?.sky?.period!=='day') notes.push('things: nothing glows and nothing casts light, so the planet is dim at night; give something a light or a glowing part');
  return notes;
}

// ---------- A planet in an address ----------
// A preview is kept nowhere: the spec travels in the address of the preview page, after the #
// (never sent to a server), squeezed (deflate) and written in base64url. A 64 KB spec is a few
// KB this way. The Planets connector packs it (preview_planet); the world unpacks it and checks
// it again before drawing anything.
export const PREVIEW_PAGE = 'https://stevenpisani.com/p/preview/';

const squeeze = async (bytes, stream) => new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(stream)).arrayBuffer());

/** The spec as the text after the # of the preview page. */
export async function packPlanet(spec) {
  const bytes = await squeeze(new TextEncoder().encode(JSON.stringify(spec)), new CompressionStream('deflate-raw'));
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The spec back from packPlanet's text, or null if it isn't one (checkPlanet says whether it's a good one). */
export async function unpackPlanet(text) {
  try {
    if (!text || text.length > LIMITS.bytes * 2) return null;
    const bin = atob(text.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = await squeeze(Uint8Array.from(bin, (c) => c.charCodeAt(0)), new DecompressionStream('deflate-raw'));
    if (bytes.length > LIMITS.bytes * 2) return null;
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (e) {
    return null;
  }
}
